import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

export type AccountStatus = "pending" | "accepted" | "rejected";

export interface AccountStatusData {
  status: AccountStatus | null;
  aiSearchesUsed: number;
  welcomeSeen: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
  markWelcomeSeen: () => Promise<void>;
}

interface Row {
  status: AccountStatus;
  aiSearchesUsed: number;
  welcomeSeen: boolean;
}

/**
 * Loads the current user's profile status + AI search quota usage.
 * Shared through the query cache, so every component that asks gets one request and one answer
 * instead of each loading (and flashing) on its own.
 */
export const useAccountStatus = (): AccountStatusData => {
  const { session, loading: authLoading } = useAuth();
  const qc = useQueryClient();
  const userId = session?.user?.id;
  const key = ["account-status", userId];

  const { data, isPending } = useQuery({
    queryKey: key,
    enabled: !authLoading && !!userId,
    queryFn: async (): Promise<Row | null> => {
      const { data, error } = await supabase
        .from("profiles")
        .select("status, ai_searches_used, welcome_seen_at")
        .eq("id", userId!)
        .maybeSingle();
      if (error) {
        // A query error (e.g. missing column) is not the same as "no profile row yet": do not let
        // it silently gate an existing account to pending.
        console.warn("useAccountStatus: failed to load profile", error);
        return null;
      }
      return {
        status: ((data?.status as AccountStatus) ?? "pending") as AccountStatus,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        aiSearchesUsed: Number((data as any)?.ai_searches_used ?? 0),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        welcomeSeen: Boolean((data as any)?.welcome_seen_at),
      };
    },
  });

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ["account-status", userId] });
  }, [qc, userId]);

  const markWelcomeSeen = useCallback(async () => {
    if (!userId) return;
    qc.setQueryData<Row | null>(["account-status", userId], (old) => (old ? { ...old, welcomeSeen: true } : old));
    await supabase
      .from("profiles")
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .update({ welcome_seen_at: new Date().toISOString() } as any)
      .eq("id", userId);
  }, [qc, userId]);

  return {
    status: data?.status ?? null,
    aiSearchesUsed: data?.aiSearchesUsed ?? 0,
    welcomeSeen: data?.welcomeSeen ?? false,
    loading: authLoading || (!!userId && isPending),
    refresh,
    markWelcomeSeen,
  };
};
